import {
  Directive,
  HostListener,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { Menu } from 'primeng/menu';
import { Table, TableColumnReorderEvent } from 'primeng/table';
import { AuthenticationService } from '../../../core/auth/services/authentication.service';
import { IFilterInput } from '../../interfaces/filter-input.interface';
import { GridColumnConfig } from '../../interfaces/grid-column-config.interface';
import { MenuItem } from 'primeng/api';
import { ColumnSort } from '../../interfaces/shared';

// tagName is used because the elements belong to another window, so instanceof would fail.
function whenLoaded(
  element: HTMLLinkElement | HTMLImageElement,
): Promise<void> {
  const loaded =
    element.tagName === 'IMG'
      ? (element as HTMLImageElement).complete
      : !!(element as HTMLLinkElement).sheet;
  if (loaded) return Promise.resolve();
  return new Promise((resolve) => {
    element.addEventListener('load', () => resolve(), { once: true });
    element.addEventListener('error', () => resolve(), { once: true });
  });
}

@Directive()
export abstract class BaseTableComponent {
  abstract getGridData(filters?: any): void;

  @ViewChild('firstDataTable') firstDataTable: Table | undefined;

  public selectedGridColumns: any[] = [];
  public selectedItems: any = [];
  public gridColumn: GridColumnConfig[] = [];

  constructor(
    protected authenticationService: AuthenticationService,
    protected _renderer: Renderer2,
  ) {
    this.initializeColumnSortMenu();
  }
  /**
   * Filter modes available in the grid
   */
  public filterModes: any[] = [
    { label: 'Contains', value: 'contains' },
    { label: 'Equals', value: 'equals' },
  ];

  /**
   * State management for loading and filters
   */
  public loading: boolean = true;
  public filterValue: any;
  public rows = signal<number>(10);
  public first = signal<number>(0);
  public pageNumber = signal<number>(0);
  public totalRecords = signal<number>(0);
  public searchValue: string | undefined;
  public rowsPerPageOptions: number[] = [10, 25, 50, 100];
  public activityValues: number[] = [0, 100];
  public filtersData: IFilterInput[] = [];
  public updatedFiltersData = signal<IFilterInput[]>([]);
  public isContractorModalVisible = signal<boolean>(false); // Control the contractor modal's visibility
  public isDataIntegrityModalVisible = signal<boolean>(false); // Control the DataIntegrityModal modal's visibility
  public noDataMessage = signal<string>('There are no records available.');
  public statesData: { label: string; value: string }[] = [];
  public periodsData: { label: string; value: string }[] = [];
  public PartnersData: { label: string; value: string }[] = [];
  public tableScrollHeight = signal<string>('40vh'); // Default scroll height
  public filtersValues = signal<any>({});
  public currentPage = signal<number>(1);
  public columnSortMenuItems: MenuItem[] = [];
  public activeSortField = '';
  public activeSortMenuField = signal<string | null>(null);
  public isSwitchingSortMenu = false;

  /**
   * window resize event
   */
  @HostListener('window:resize', [])
  onWindowResize() {
    this.updateTableHeight(); // Recalculate on window resize
  }

  /**
   * Clears all filters and resets the grid state
   */
  public clear(table: Table): void {
    table.clear();
    this.searchValue = '';
  }

  /**
   * Performs a global search in the grid
   */
  public search(event: any): void {
    if (this.firstDataTable) {
      this.firstDataTable.filterGlobal(event.target.value, 'contains');
    }
  }

  /**
   * Applies a filter to a specific field in the grid
   */
  public applyFilter(field: string, mode: string): void {
    if (this.firstDataTable) {
      this.firstDataTable.filter(this.filterValue, field, mode);
    }
  }

  /**
   * Clears a specific filter in the grid
   */
  public clearFilter(field: string): void {
    this.filterValue = null;
    if (this.firstDataTable) {
      this.firstDataTable.filter('', field, 'contains');
    }
  }

  /**
   * Handles column reordering in the grid
   */
  public colReorder(ev: TableColumnReorderEvent): void {
    this.gridColumn = [...this.gridColumn];
  }

  /**
   * Updates the grid view based on selected columns
   */
  public changeGridView(): void {
    if (!this.selectedGridColumns.length) {
      this.selectedGridColumns.push(this.gridColumn[0]);
    }

    if (
      this.selectedGridColumns.some(
        (c) => !this.gridColumn.some((g) => g.field === c.field),
      )
    ) {
      this.selectedGridColumns = this.gridColumn.filter((c) => c.showColumn);
    }

    this.gridColumn.forEach((element) => {
      element.showColumn = this.selectedGridColumns.some(
        (column) => column.field === element.field,
      );
    });
  }

  /**
   * reset rows and first
   */
  public resetPaginationItems(): void {
    this.rows.set(10);
    this.first.set(0);
    this.totalRecords.set(0);
  }

  /**
   * format the mobile number and wrap the first three digits in parentheses
   */
  formatMobileNumber(mobileNumber: string): string {
    // Format the number
    return `(${mobileNumber.slice(0, 3)}) ${mobileNumber.slice(3, 6)}-${mobileNumber.slice(6)}`;
  }

  public toggleMenu(event: Event, menu: Menu) {
    menu.toggle(event);

    this._renderer.setStyle(
      menu.el.nativeElement,
      'top',
      `${(event as any).y}px !important`,
    );
    this._renderer.setStyle(
      menu.el.nativeElement,
      'left',
      `${(event as any).x}px !important`,
    );
  }

  /**
   * Updates the table height based on the window width
   */
  updateTableHeight() {
    const windowWidth = window.innerWidth;
    // Adjust height based on window width
    if (windowWidth < 768) {
      this.tableScrollHeight.set('30vh');
    } else this.tableScrollHeight.set('57vh');
  }

  public printMainRouterOutlet(zoom?: number): void {
    const printContent = document.querySelector('.main-router-outlet');

    if (!printContent) {
      return;
    }

    this.printElement(
      printContent,
      `@page { margin: 0; } body { zoom: ${zoom ?? 0.5}; margin: 0; }`,
    );
  }

  /**
   * Opens a print window containing a copy of the element and the page styles,
   * and prints once its stylesheets and images have loaded.
   */
  protected printElement(content: Element, printCss: string): void {
    const printWindow = window.open('', '_blank', 'width=900,height=650');

    if (!printWindow) {
      return;
    }

    const printDocument = printWindow.document;
    printDocument.title = 'Print Preview';
    document
      .querySelectorAll('link[rel="stylesheet"], style')
      .forEach((node) =>
        printDocument.head.appendChild(printDocument.importNode(node, true)),
      );

    const printStyle = printDocument.createElement('style');
    printStyle.textContent = `@media print { ${printCss} body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`;
    printDocument.head.appendChild(printStyle);
    printDocument.body.appendChild(printDocument.importNode(content, true));

    const resources = Array.from(
      printDocument.querySelectorAll<HTMLLinkElement | HTMLImageElement>(
        'link[rel="stylesheet"], img',
      ),
    );
    void Promise.all(resources.map(whenLoaded)).then(() => {
      printWindow.print();
      printWindow.close();
      window.focus();
    });
  }

  public getNextOrder(field: string): number {
    const current = this.filtersValues();
    if (current?.sort_field === field) {
      return current.sort_value === 'asc' ? -1 : 1;
    }
    return 1;
  }

  public addGlobalIndex<T>(items: T[]): (T & { index: number })[] {
    const offset = (this.currentPage() - 1) * this.rows();
    return items.map((item, i) => ({
      index: offset + i + 1,
      RowId: offset + i + 1,
      ...item,
    }));
  }

  public initializeColumnSortMenu(): void {
    this.columnSortMenuItems = [
      {
        label: 'Sort Ascending',
        icon: 'icon-sort-ascending',
        command: () =>
          this.onColumnSort({ field: this.activeSortField, order: 1 }),
      },
      {
        label: 'Sort Descending',
        icon: 'icon-sort-descending',
        command: () =>
          this.onColumnSort({ field: this.activeSortField, order: -1 }),
      },
    ];
  }

  public onSortButtonClick(field: string, menu: any, event: Event): void {
    this.activeSortField = field;
    this.isSwitchingSortMenu = true;
    this.activeSortMenuField.set(null);
    menu.toggle(event);

    setTimeout(() => {
      this.activeSortMenuField.set(field);
      this.isSwitchingSortMenu = false;
    });
  }

  public onSortMenuHide(): void {
    if (this.isSwitchingSortMenu) return;
    setTimeout(() => this.activeSortMenuField.set(null), 150);
  }

  public getSortIcon(field: string): string {
    const sortField = this.filtersValues()?.sort_field;
    const sortOrder = this.filtersValues()?.sort_value;

    if (sortField === field) {
      return sortOrder === 'asc'
        ? 'icon-sort-ascending'
        : 'icon-sort-descending';
    }
    return 'icon-arrows-down-up';
  }

  public isSelectedSort(label: string): boolean {
    const { sort_field, sort_value } = this.filtersValues() ?? {};
    if (this.activeSortField !== sort_field) return false;
    return (
      (label === 'Sort Ascending' && sort_value === 'asc') ||
      (label === 'Sort Descending' && sort_value === 'desc')
    );
  }

  public onColumnSort(data: ColumnSort): void {
    this.first.set(0);

    const currentSort = {
      sort_field: data.field,
      sort_value: data.order === 1 ? 'asc' : 'desc',
    };

    const currentFilters = this.filtersValues();

    if (
      currentFilters.sort_field === currentSort.sort_field &&
      currentFilters.sort_value === currentSort.sort_value
    ) {
      return;
    }

    const updatedFilters = {
      ...currentFilters,
      ...currentSort,
    };

    this.filtersValues.set(updatedFilters);
    const page = Math.floor(this.first() / this.rows()) + 1;
    this.currentPage.set(page);

    this.getGridData(updatedFilters);
  }
}
